import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import {
	AdaptiveSingleLineText,
	Guideline,
	Icons,
	Select,
	SpinnerOverlay,
} from '@components/common';
import RadioButton from '@components/common/Form/RadioButton';
import NavigateButtons from '@components/common/NavigateButtons';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { uuid } from '@utils/common';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISaveImportRulesDTO } from '@interfaces/import/dto/importDTO';
import { IImportRulesValues } from '@interfaces/import/import';

import { ESnackbarTypes } from '@enums/common';
import { EImportingDataType } from '@enums/dropzone';
import { EValidationMode } from '@enums/validationMode';

import { MAX_IMPORT_LINES } from '@constants/common';
import { responseMessages } from '@constants/responseMessages';
import { skipEmptyFieldsOptions } from './constants';
import { NEXT_DISABLED_TOOLTIP_TEXT } from '@components/ModalComponents/ImportMapDataContainer/constants';
import { componentNames } from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/constants';

import { schema } from './schema';

import styles from './ImportRules.module.scss';

export interface IImportRulesProps {
	setCurrentComponent: React.Dispatch<React.SetStateAction<string>>;
	setIsDeleteRule: React.Dispatch<React.SetStateAction<boolean>>;
	onClose: () => void;
	fileName: string | undefined;
	importTypes: Array<TSelectOption>;
	allFields: Array<TSelectOption>;
	saveImportRules: (data: ISaveImportRulesDTO & { nextClickHandler: () => void }) => void;
	importIds: string[];
	isLoadingImportRules: boolean;
	numRows?: number;
	name?: string;
}

const ImportRules = ({
	setCurrentComponent,
	onClose,
	fileName,
	importTypes,
	allFields,
	saveImportRules,
	importIds,
	isLoadingImportRules,
	setIsDeleteRule,
	numRows,
	name = EImportingDataType.asset,
}: IImportRulesProps) => {
	const dispatch = useAppDispatch();

	const {
		handleSubmit,
		formState: { errors },
		control,
		setValue,
		watch,
	} = useForm<IImportRulesValues>({
		mode: EValidationMode.all,
		resolver: yupResolver(schema),
		defaultValues: {
			importType: null,
			skipEmptyFields: false,
			skipFields: null,
		},
	});

	const isNextDisabled = !watch('importType')?.value;
	const isDelete = watch('importType')?.value === 'DELETE';
	const skipFields = watch('skipFields');

	const handleNextClick = () => {
		setCurrentComponent(componentNames.fieldsMapping);
	};

	const onSubmit = (values: IImportRulesValues) => {
		const isEmptyFile = !allFields.length || (numRows && (numRows <= 0));
		const isOversizeFile = !allFields.length && (numRows && (numRows > MAX_IMPORT_LINES));

		if (isOversizeFile) {
			dispatch(setSnackbar({
				message: responseMessages.oversizeFile,
				type: ESnackbarTypes.error,
			}));
		} else if (isEmptyFile) {
			dispatch(setSnackbar({
				message: responseMessages.emptyFile,
				type: ESnackbarTypes.error,
			}));
		} else {
			setIsDeleteRule(isDelete);

			const submitData = {
				importType: values.importType?.value,
				skipEmptyFields: isDelete ? null : values.skipEmptyFields,
				skipFields: isDelete
					? null
					: values?.skipFields?.map((field: TSelectOption) => field?.value),
			};

			saveImportRules({
				id: importIds[0],
				nextClickHandler: handleNextClick,
				...submitData,
			} as any);
		}
	};

	const changeSkipFields = (options: Array<TSelectOption> | unknown) => {
		setValue('skipFields', options as Array<TSelectOption>);
	};

	const isAllFieldsSkip = !!(allFields.length && skipFields?.length && allFields.length === skipFields.length);

	return (
		<div className={styles.importRulesContainer}>
			{isLoadingImportRules && <SpinnerOverlay />}
			<div className={styles.fileName}>
				<span>{Icons.file()}</span>
				{!!fileName
					&& <AdaptiveSingleLineText
						className={styles.p0}
						maxWidth={450}
						paddingsSize={0}
						text={fileName}
					/>
				}
			</div>
			<div className={styles.selectFields}>
				<div>
					<Select
						control={control}
						errorMessage={(errors.importType as any)?.message}
						isError={!!errors?.importType}
						label='Import Type'
						name='importType'
						options={importTypes}
						placeholder='Select import type'
						required
						tooltipContent={
							<>
								Update - the fields for existing {name} ID in the database will be updated.
								Nonexistent {name} ID in database will be skipped.
								<br />
								<br />
								Add - nonexistent {name} ID in the database will be added. Existed {name} ID will be
								skipped.
								<br />
								<br />
								Add & Update - the fields of existing {name} ID in the database will be updated.
								<br />
								Nonexistent {name} ID in database will be added.
								{EImportingDataType.asset == name &&
									<>
										<br />
										<br />
										Delete - all existed {name} ID in the database will be deleted.
									</>
								}
							</>
						}
					/>
				</div>
				{!isDelete
					&& <div>
						<Select
							closeMenuOnSelect={false}
							control={control}
							errorMessage={(errors.skipFields as any)?.message}
							handleChange={changeSkipFields}
							isClearable
							isError={!!errors?.skipFields}
							isMulti
							label='Skip Fields'
							name='skipFields'
							options={allFields}
							placeholder='Select fields to skip'
							tooltipContent={`Skipped fields will be ignored for all ${name}s when importing`}
							value={skipFields}
						/>
					</div>
				}
			</div>
			<div className={styles.skipEmptyFields}>
				{!isDelete
					&& <>
						<div className={styles.title}>
							<span>Skip empty fields</span>
							<Guideline
								guidelineText={`Empty fields will be ignored for all ${name}s when importing`}
								placement='top-start'
							/>
						</div>
						<div className={styles.radio}>
							{skipEmptyFieldsOptions.map((option) => {
								const onChangeStatus = () => setValue('skipEmptyFields', option.value);

								return (
									<RadioButton
										key={uuid()}
										defaultChecked={option.value === watch('skipEmptyFields')}
										id={option.id}
										label={option.label}
										name='skipEmptyFields'
										onChange={onChangeStatus}
										withUseForms
									/>
								);
							})
							}
						</div>
					</>
				}
			</div>
			<NavigateButtons
				disabledNext={isNextDisabled || isAllFieldsSkip}
				onCloseHandler={onClose}
				onNextHandler={handleSubmit(onSubmit)}
				tooltipNext={isAllFieldsSkip ? NEXT_DISABLED_TOOLTIP_TEXT : undefined}
			/>
		</div>
	);
};

export default ImportRules;
